import { Dispatch, useEffect, useState } from "react";
import { Nullable, TriggeredHook } from "sonobello.utilities.react";
import { Optional } from "sonobello.utilities.react.mui";

import { EnhancedCenter } from "../../../../Models/Center";
import { EnhancedCustomer } from "../../../../Models/Customer";
import { Error } from "../../../../Utils/Errors";
import { useApi } from "../../../../Utils/useApi";
import { getCustomerSearchPath } from "../../../Constants/ApiRequestPaths";
import { AreaOfInterest } from "../../Interests/Types/AreaOfInterest";
import V2Customer from "../Types/V2Customer";

export interface IUseGetCustomerProps {
  /** All possible areas of interest which might be selectable in the client. */
  areaOfInterestOptions: AreaOfInterest[];
  /** The possible centers to which the customer could belong. */
  centerOptions: EnhancedCenter[];
}

export interface IUseGetCustomerRequest {
  /** The id of the customer being sought. */
  customerId: string;
  /** The id of the contact to which the customer belongs. */
  contactId: string;
}

export interface IUseGetCustomerResponse {
  /** The response of the customer fetch. */
  customer: Optional<EnhancedCustomer>;
  /** Flag indicating if the hook is currently pending completion. */
  isLoading: boolean;
  /** Fetch a customer with the given id. */
  setRequest: Dispatch<IUseGetCustomerRequest>;
}

/** Fetches a customer with the requested matching params. */
const useGetCustomer: TriggeredHook<EnhancedCustomer, IUseGetCustomerRequest, boolean, IUseGetCustomerProps> = ({
  areaOfInterestOptions,
  centerOptions
}: IUseGetCustomerProps) => {
  const [request, setRequest] = useState<Nullable<IUseGetCustomerRequest>>(null);
  const [result, setResult] = useState<Nullable<EnhancedCustomer>>(null);

  const { response, isLoading, error } = useApi<V2Customer>({
    url: request ? getCustomerSearchPath(request.contactId, request.customerId) : undefined,
    isCancelable: false,
    valid: !!request,
    error: Error.GetCustomer
  });

  useEffect(() => {
    if (response) setResult(EnhancedCustomer.fromV2Customer(response, areaOfInterestOptions, centerOptions));
  }, [response]);

  return {
    result,
    isLoading,
    error: Boolean(error),
    execute: setRequest
  };
};

export default useGetCustomer;
